// Writes run one at a time: the app read-modify-writes the showroom JSON, and the
// storefront serves stale metaobjects for a while, so callers use refreshFromPage().

export const getShowroomConfig = () => window.showroomConfig || {};

export const escapeHtml = (value = "") =>
  String(value).replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]);

export const toNumericId = (id) => {
  if (id === undefined || id === null) return "";

  return String(id).split("/").pop();
};

const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

// Writes run one after another; `writesInFlight` keeps a refresh from
// swapping in markup while a write is still being saved.
let writeChain = Promise.resolve();
let writesInFlight = 0;

export function showroomRequest(endpoint, options = {}) {
  if ((options.method || "POST") !== "POST") return sendShowroomRequest(endpoint, options);

  writesInFlight += 1;
  const run = writeChain.then(() => sendShowroomRequest(endpoint, options));
  writeChain = run.catch(() => {});

  return run.finally(() => {
    writesInFlight -= 1;
  });
}

async function sendShowroomRequest(endpoint, { body = {}, showroom, method = "POST" } = {}) {
  const { api, customer } = getShowroomConfig();

  if (!api || !customer?.email) {
    return { ok: false, data: null, error: "Showroom is not available." };
  }

  const headers = {
    "Content-Type": "application/json",
    email: encodeURIComponent(customer.email),
  };

  const showroomHandle = showroom ?? customer.showroom;
  if (showroomHandle) headers.showroom = showroomHandle;

  let url = `${api}/${endpoint}`;

  if (method === "GET") {
    const params = new URLSearchParams(body);
    url = params.toString() ? `${url}?${params}` : url;
  }

  try {
    const response = await fetch(url, {
      method,
      headers,
      body: method === "POST" ? JSON.stringify(body) : null,
    });

    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok) {
      return {
        ok: false,
        data,
        // The app sends a thrown error's text ("Error: …"); show the message only.
        error: String(data?.errorMessage || data?.error || `Request failed with status ${response.status}`).replace(
          /^Error:\s*/,
          "",
        ),
      };
    }

    return { ok: true, data, error: null };
  } catch (error) {
    console.error(`Showroom request "${endpoint}" failed.`, error);
    return { ok: false, data: null, error: error.message };
  }
}

// Fetches the current page and returns it as a parsed document.
export async function fetchCurrentPage() {
  const url = new URL(window.location.href);
  // Bust any intermediate cache so the latest metaobject values come back.
  url.searchParams.set("_", Date.now().toString());

  const response = await fetch(url.toString(), { credentials: "same-origin" });
  const html = await response.text();

  return new DOMParser().parseFromString(html, "text/html");
}

// The page's [data-showroom-state] JSON: what the page was rendered from,
// patched by callers right after a successful write so counts and button
// states are right before the fresh markup arrives.
export function readShowroomState(root = document) {
  try {
    return JSON.parse(root.querySelector("[data-showroom-state]")?.textContent || "{}");
  } catch {
    return {};
  }
}

export function updateShowroomState(update, root = document) {
  const script = root.querySelector("[data-showroom-state]");
  if (!script) return;

  script.textContent = JSON.stringify(update(readShowroomState(root)) || {});
}

// Every change still waiting to show up in the storefront's copy of the page:
// { selectors, isReady(doc) }. One sync loop serves them all.
const pendingChanges = new Set();
let syncRun = null;
let syncActive = false;

// Backs off from about a second to ten, about a minute in all.
const SYNC_DELAYS = [700, 1000, 1500, 2000, 2500, 3000, 4000, 5000, 6000, 8000, 10000, 10000];

async function runSync() {
  syncActive = true;
  document.documentElement.classList.add("is-showroom-syncing");

  for (const delay of SYNC_DELAYS) {
    await wait(delay);
    if (!pendingChanges.size) break;
    if (writesInFlight > 0) continue;

    let doc;
    try {
      doc = await fetchCurrentPage();
    } catch (error) {
      console.error("Showroom refresh failed.", error);
      continue;
    }

    const changes = [...pendingChanges];
    if (!changes.every((change) => change.isReady(doc))) continue;

    const selectors = [...new Set(changes.flatMap((change) => change.selectors))];
    selectors.forEach((selector) => {
      const fresh = doc.querySelector(selector);
      const current = document.querySelector(selector);
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    });

    changes.forEach((change) => pendingChanges.delete(change));
    document.dispatchEvent(new CustomEvent("showroom:refreshed", { detail: { selectors } }));
    if (!pendingChanges.size) break;
  }

  // Set synchronously so a change added from here on starts a new run.
  syncActive = false;
  document.documentElement.classList.remove("is-showroom-syncing");

  // Never caught up: the changes are saved, the page just can't show them yet.
  if (pendingChanges.size) {
    pendingChanges.clear();
    showroomToast(getShowroomConfig().strings?.syncDelayed);
    return false;
  }

  return true;
}

// Waits until the page reflects this change (and every other pending one),
// then swaps each selector's markup in. Resolves true once shown, false if
// the storefront didn't catch up in time (the page is left as it is).
export function refreshFromPage(selectors, { isReady = () => true } = {}) {
  const change = { selectors: Array.isArray(selectors) ? selectors : [selectors], isReady };
  pendingChanges.add(change);

  if (!syncActive) syncRun = runSync();
  const run = syncRun;

  return new Promise((resolve) => {
    const onRefreshed = () => {
      if (pendingChanges.has(change)) return;
      document.removeEventListener("showroom:refreshed", onRefreshed);
      resolve(true);
    };

    document.addEventListener("showroom:refreshed", onRefreshed);
    run.then(() => {
      document.removeEventListener("showroom:refreshed", onRefreshed);
      resolve(false);
    });
  });
}

// For changes that touch too much of the page to patch (the hero, nav, every
// image of a recoloured style): waits until the page reflects the change,
// then reloads once.
export async function reloadWhenReady(isReady) {
  const shown = await refreshFromPage([], { isReady });
  if (shown) window.location.reload();
  return shown;
}

// Resolves true only when deleted and the shopper stays on this page (otherwise
// they're redirected to their next active showroom or the landing page).
export async function deleteShowroom(handle) {
  const config = getShowroomConfig();
  const strings = config.strings || {};
  if (!handle || !window.confirm(strings.deleteConfirm)) return false;

  const { ok, data, error } = await showroomRequest("deleteShowroom", { body: { handle }, showroom: handle });

  if (!ok) {
    showroomToast(error && error !== "Request failed with status 400" ? error : strings.deleteError || strings.genericError);
    return false;
  }

  const viewing = document.querySelector("showroom-page")?.dataset.showroomId === handle;
  const wasActive = config.customer?.showroom === handle;

  if (viewing || wasActive) {
    const next = data?.activeShowroom || "";
    if (config.customer) config.customer.showroom = next;
    window.location.href = next ? `${config.pageUrl}?id=${encodeURIComponent(next)}` : config.landingUrl;
    return false;
  }

  showroomToast(strings.deleted);
  return true;
}

export const showroomToast = (message) => {
  if (message && typeof window.showToast === "function") {
    window.showToast(message);
  }
};
